import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../lib/api'
import { keys, useMembers, useProjects, useStatuses, useUsers } from '../lib/queries'
import type { Project, ProjectRole, Status, User } from '../lib/types'
import {
  Avatar, Button, EmptyState, Field, Input, Section, Select, Spinner, Textarea, cx, useToast,
} from '../components/ui'

const PROJECT_EVENTS: Array<{ id: string; label: string }> = [
  { id: 'task_created', label: 'A task is created' },
  { id: 'task_done', label: 'A task reaches a done column' },
  { id: 'urgent_created', label: 'An urgent task is created' },
]

export function ProjectSettingsPage({ user }: { user: User }) {
  const { key = '' } = useParams()
  const project = useQuery({
    queryKey: ['project-by-key', key],
    queryFn: () => api.projectByKey(key),
    retry: false,
  })

  if (project.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner className="h-5 w-5 text-[var(--ink-3)]" />
      </div>
    )
  }

  if (!project.data || project.data.myRole !== 'owner') {
    return (
      <EmptyState
        title="Only project owners can change these settings"
        hint="Ask an owner to make the change, or to make you an owner."
        action={
          <Link to={`/p/${key}`}>
            <Button>Back to the board</Button>
          </Link>
        }
      />
    )
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[680px] px-5 py-6">
        <Link
          to={`/p/${key}`}
          className="text-[12px] text-[var(--ink-3)] transition-colors duration-[140ms] hover:text-[var(--ink)]"
        >
          ← {project.data.name}
        </Link>
        <h1 className="mt-1 text-[17px] font-semibold tracking-tight">Project settings</h1>

        <DetailsSection project={project.data} />
        <ColumnsSection project={project.data} />
        <MembersSection project={project.data} user={user} />
        <SlackSection project={project.data} />
        <MaintenanceSection project={project.data} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- details

function DetailsSection({ project }: { project: Project }) {
  const [name, setName] = useState(project.name)
  const [key, setKey] = useState(project.key)
  const [description, setDescription] = useState(project.description)
  const [color, setColor] = useState(project.color)
  const [parentId, setParentId] = useState(project.parentId ?? '')
  const queryClient = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const projects = useProjects()

  // One level deep: a project that already has sub-projects cannot become one,
  // and only top-level projects can be parents.
  const canHaveParent = project.childCount === 0
  const parentOptions = (projects.data ?? []).filter(
    (p) => p.id !== project.id && !p.parentId && !p.archivedAt,
  )

  const save = useMutation({
    mutationFn: () =>
      api.updateProject(project.id, {
        key, name, description, color,
        slackChannelId: project.slackChannelId,
        slackEvents: project.slackEvents,
        ...(canHaveParent ? { parentId: parentId || null } : {}),
      }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: keys.projects() })
      queryClient.invalidateQueries({ queryKey: ['project-by-key'] })
      toast.show('Project saved')
      if (updated.key !== project.key) navigate(`/p/${updated.key}/settings`, { replace: true })
    },
  })
  const error = save.error instanceof ApiError ? save.error : null

  return (
    <Section title="Details">
      <div className="space-y-4">
        <Field label="Name" htmlFor="p-name" error={error?.fields?.name}>
          <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>

        <Field
          label="Key" htmlFor="p-key" error={error?.fields?.key}
          hint="Changing this renames every task key in the project, all at once."
        >
          <Input
            id="p-key" value={key} maxLength={10}
            onChange={(e) => setKey(e.target.value.toUpperCase())}
            className="task-key uppercase"
          />
        </Field>

        <Field
          label="Part of" htmlFor="p-parent" error={error?.fields?.parentId}
          hint={
            canHaveParent
              ? 'Make this a service inside a bigger project. That project’s board then shows everything underneath it.'
              : `This project already has ${project.childCount} sub-project${project.childCount === 1 ? '' : 's'}, so it cannot become one itself.`
          }
        >
          <Select
            id="p-parent"
            value={parentId}
            disabled={!canHaveParent}
            onChange={(e) => setParentId(e.target.value)}
          >
            <option value="">Nothing — this is a top-level project</option>
            {parentOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Description" htmlFor="p-desc">
          <Textarea
            id="p-desc" rows={2} value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <Field label="Colour" htmlFor="p-color" error={error?.fields?.color}>
          <div className="flex items-center gap-2">
            <input
              id="p-color" type="color" value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-8 w-12 cursor-pointer rounded border border-[var(--line)] bg-transparent"
            />
            <span className="task-key text-[12px] text-[var(--ink-3)]">{color}</span>
          </div>
        </Field>

        <Button variant="primary" onClick={() => save.mutate()} loading={save.isPending}>
          Save changes
        </Button>
      </div>
    </Section>
  )
}

// ---------------------------------------------------------------- columns

function ColumnsSection({ project }: { project: Project }) {
  const statuses = useStatuses(project.id)
  const queryClient = useQueryClient()
  const toast = useToast()
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')

  function refresh() {
    queryClient.invalidateQueries({ queryKey: keys.statuses(project.id) })
    queryClient.invalidateQueries({ queryKey: ['board'] })
  }

  const create = useMutation({
    mutationFn: () => api.createStatus(project.id, { name: newName, color: '#64748b', isDone: false }),
    onSuccess: () => {
      refresh()
      setNewName('')
      setAdding(false)
    },
  })

  const update = useMutation({
    mutationFn: (s: Status) =>
      api.updateStatus(project.id, s.id, { name: s.name, color: s.color, isDone: s.isDone }),
    onSuccess: refresh,
  })

  const reorder = useMutation({
    mutationFn: (order: string[]) => api.reorderStatuses(project.id, order),
    onSuccess: refresh,
  })

  const remove = useMutation({
    mutationFn: ({ id, moveTo }: { id: string; moveTo?: string }) =>
      api.deleteStatus(project.id, id, moveTo),
    onSuccess: refresh,
    onError: (e) => {
      const message =
        e instanceof ApiError ? Object.values(e.fields ?? {})[0] ?? e.message : 'Could not remove that column'
      toast.show(message, { tone: 'error' })
    },
  })

  const list = statuses.data ?? []

  function move(index: number, delta: number) {
    const next = [...list]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    const [item] = next.splice(index, 1)
    next.splice(target, 0, item!)
    reorder.mutate(next.map((s) => s.id))
  }

  return (
    <Section
      title="Columns"
      hint="These are the board's statuses. Cards in a done column count as finished and stop appearing as overdue."
    >
      <div className="space-y-1.5">
        {list.map((status, index) => (
          <div
            key={status.id}
            className="flex items-center gap-2 rounded-md border border-[var(--line)] px-2.5 py-1.5"
          >
            <div className="flex flex-col">
              <button
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label={`Move ${status.name} left`}
                className="text-[9px] leading-none text-[var(--ink-3)] hover:text-[var(--ink)] disabled:opacity-30"
              >
                ▲
              </button>
              <button
                onClick={() => move(index, 1)}
                disabled={index === list.length - 1}
                aria-label={`Move ${status.name} right`}
                className="text-[9px] leading-none text-[var(--ink-3)] hover:text-[var(--ink)] disabled:opacity-30"
              >
                ▼
              </button>
            </div>

            <input
              type="color"
              value={status.color}
              onChange={(e) => update.mutate({ ...status, color: e.target.value })}
              aria-label={`Colour for ${status.name}`}
              className="h-6 w-8 cursor-pointer rounded border border-[var(--line)] bg-transparent"
            />

            <input
              defaultValue={status.name}
              onBlur={(e) =>
                e.target.value.trim() &&
                e.target.value !== status.name &&
                update.mutate({ ...status, name: e.target.value.trim() })
              }
              aria-label={`Name for ${status.name}`}
              className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-[13px] outline-none hover:border-[var(--line)] focus:border-[var(--accent)]"
            />

            <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-[var(--ink-2)]">
              <input
                type="checkbox"
                checked={status.isDone}
                onChange={(e) => update.mutate({ ...status, isDone: e.target.checked })}
                className="accent-[var(--accent)]"
              />
              Done column
            </label>

            <button
              onClick={() => {
                const others = list.filter((s) => s.id !== status.id)
                const target = others[0]
                if (!target) return
                if (
                  window.confirm(
                    `Remove “${status.name}”? Any tasks in it move to “${target.name}”.`,
                  )
                ) {
                  remove.mutate({ id: status.id, moveTo: target.id })
                }
              }}
              disabled={list.length <= 1}
              className="text-[11px] text-[var(--ink-3)] hover:text-[var(--danger)] disabled:opacity-30"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      {adding ? (
        <div className="mt-2 flex items-center gap-2">
          <Input
            autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="Column name"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) create.mutate()
              if (e.key === 'Escape') setAdding(false)
            }}
          />
          <Button variant="primary" onClick={() => create.mutate()} loading={create.isPending}>
            Add
          </Button>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      ) : (
        <Button className="mt-2" onClick={() => setAdding(true)}>
          Add a column
        </Button>
      )}
    </Section>
  )
}

// ---------------------------------------------------------------- members

function MembersSection({ project, user }: { project: Project; user: User }) {
  const members = useMembers(project.id)
  const users = useUsers()
  const queryClient = useQueryClient()
  const toast = useToast()
  const [pick, setPick] = useState('')

  function refresh() {
    queryClient.invalidateQueries({ queryKey: keys.members(project.id) })
  }

  function onError(e: unknown) {
    const message =
      e instanceof ApiError ? Object.values(e.fields ?? {})[0] ?? e.message : 'Something went wrong'
    toast.show(message, { tone: 'error' })
  }

  const add = useMutation({
    mutationFn: (v: { userId: string; role: ProjectRole }) =>
      api.addMember(project.id, v.userId, v.role),
    onSuccess: () => {
      refresh()
      setPick('')
    },
    onError,
  })

  const remove = useMutation({
    mutationFn: (userId: string) => api.removeMember(project.id, userId),
    onSuccess: refresh,
    onError,
  })

  const setDefault = useMutation({
    mutationFn: (userId: string | null) =>
      api.updateProject(project.id, {
        name: project.name,
        description: project.description,
        color: project.color,
        slackChannelId: project.slackChannelId,
        slackEvents: project.slackEvents,
        defaultAssigneeId: userId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.projects() })
      queryClient.invalidateQueries({ queryKey: ['project-by-key'] })
      toast.show('Saved')
    },
    onError,
  })

  const current = new Set((members.data ?? []).map((m) => m.user.id))
  const candidates = (users.data ?? []).filter((u) => !current.has(u.id))

  return (
    <Section
      title="Members"
      hint={
        project.parentKey
          ? `Everybody on ${project.parentKey} can see this project too. Add somebody here to give them a different role in it.`
          : project.childCount > 0
            ? 'Members here can see every sub-project as well. A project with just you in it is your private list.'
            : 'Only members can see this project. A project with just you in it is your private list.'
      }
    >
      <div className="space-y-1.5">
        {(members.data ?? []).map((m) => (
          <div
            key={m.user.id}
            className="flex items-center gap-2.5 rounded-md border border-[var(--line)] px-2.5 py-1.5 text-[13px]"
          >
            <Avatar user={m.user} size={24} />
            <div className="min-w-0">
              <p className="truncate leading-tight">{m.user.name}</p>
              <p className="task-key truncate text-[11px] leading-tight text-[var(--ink-3)]">
                {m.user.email}
              </p>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Select
                value={m.role}
                onChange={(e) =>
                  add.mutate({ userId: m.user.id, role: e.target.value as ProjectRole })
                }
                className="h-7 w-[92px] py-0 text-[12px]"
                aria-label={`Role for ${m.user.name}`}
              >
                <option value="member">Member</option>
                <option value="owner">Owner</option>
              </Select>
              {/* Inherited access is held on the parent, so it is removed there.
                  Picking a role above writes a row here that overrides it. */}
              {m.inherited ? (
                <span
                  className="task-key whitespace-nowrap text-[11px] text-[var(--ink-3)]"
                  title={`Added on ${project.parentName || project.parentKey} — remove them there`}
                >
                  via {project.parentKey}
                </span>
              ) : (
                <button
                  onClick={() => remove.mutate(m.user.id)}
                  className={cx(
                    'text-[11px] text-[var(--ink-3)] hover:text-[var(--danger)]',
                    m.user.id === user.id && 'opacity-60',
                  )}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {candidates.length > 0 && (
        <div className="mt-3 flex items-end gap-2">
          <div className="flex-1">
            <Field label="Add somebody" htmlFor="add-member">
              <Select id="add-member" value={pick} onChange={(e) => setPick(e.target.value)}>
                <option value="">Choose a person…</option>
                {candidates.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} — {u.email}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Button
            variant="primary"
            disabled={!pick}
            loading={add.isPending}
            onClick={() => add.mutate({ userId: pick, role: 'member' })}
          >
            Add
          </Button>
        </div>
      )}

      {/* Most boards have one person who picks things up. Naming them here is
          what lets the create box offer them as the obvious answer instead of
          leaving a column full of cards nobody has agreed to own. */}
      <div className="mt-5 border-t border-[var(--line)] pt-4">
        <Field
          label="Responsible for this project"
          htmlFor="p-default-assignee"
          hint="New tasks go to them unless whoever creates one picks somebody else."
        >
          <Select
            id="p-default-assignee"
            value={project.defaultAssigneeId ?? ''}
            disabled={setDefault.isPending}
            onChange={(e) => setDefault.mutate(e.target.value || null)}
          >
            <option value="">Nobody — new tasks start unassigned</option>
            {(members.data ?? []).map((m) => (
              <option key={m.user.id} value={m.user.id}>
                {m.user.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Section>
  )
}

// ------------------------------------------------------------------ slack

function SlackSection({ project }: { project: Project }) {
  const [channel, setChannel] = useState(project.slackChannelId)
  const [events, setEvents] = useState<string[]>(project.slackEvents ?? [])
  const queryClient = useQueryClient()
  const toast = useToast()

  useEffect(() => {
    setChannel(project.slackChannelId)
    setEvents(project.slackEvents ?? [])
  }, [project.id, project.slackChannelId, project.slackEvents])

  const save = useMutation({
    mutationFn: () =>
      api.updateProject(project.id, {
        name: project.name,
        description: project.description,
        color: project.color,
        slackChannelId: channel,
        slackEvents: events,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-by-key'] })
      toast.show('Slack settings saved')
    },
  })

  return (
    <Section
      title="Slack channel"
      hint="Announcements for the whole team. Personal notifications are separate and follow each person's own settings."
    >
      <div className="space-y-4">
        <Field
          label="Channel ID" htmlFor="slack-channel"
          hint="In Slack, open the channel, choose View channel details, and copy the ID at the bottom. Invite the bot to that channel first."
        >
          <Input
            id="slack-channel" value={channel} onChange={(e) => setChannel(e.target.value)}
            placeholder="C0123456789" className="task-key"
          />
        </Field>

        <fieldset className="space-y-1.5">
          <legend className="mb-1 text-[12px] font-medium text-[var(--ink-2)]">Post when</legend>
          {PROJECT_EVENTS.map((e) => (
            <label key={e.id} className="flex cursor-pointer items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={events.includes(e.id)}
                onChange={(ev) =>
                  setEvents((prev) =>
                    ev.target.checked ? [...prev, e.id] : prev.filter((v) => v !== e.id),
                  )
                }
                className="accent-[var(--accent)]"
                disabled={!channel.trim()}
              />
              <span className={cx(!channel.trim() && 'text-[var(--ink-3)]')}>{e.label}</span>
            </label>
          ))}
        </fieldset>

        <Button variant="primary" onClick={() => save.mutate()} loading={save.isPending}>
          Save Slack settings
        </Button>
      </div>
    </Section>
  )
}

// ------------------------------------------------------------ maintenance

function MaintenanceSection({ project }: { project: Project }) {
  const [days, setDays] = useState(30)
  const queryClient = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()

  const archiveDone = useMutation({
    mutationFn: () => api.archiveDone(project.id, days),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['board'] })
      toast.show(
        r.archived > 0
          ? `Archived ${r.archived} completed task${r.archived === 1 ? '' : 's'}`
          : 'Nothing old enough to archive',
      )
    },
  })

  const archiveProject = useMutation({
    mutationFn: () => api.archiveProject(project.id, !project.archivedAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.projects() })
      queryClient.invalidateQueries({ queryKey: ['project-by-key'] })
      toast.show(project.archivedAt ? 'Project restored' : 'Project archived')
    },
  })

  const remove = useMutation({
    mutationFn: () => api.deleteProject(project.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.projects() })
      navigate('/my', { replace: true })
    },
  })

  return (
    <Section title="Housekeeping">
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-[190px]">
            <Field
              label="Archive completed tasks" htmlFor="archive-days"
              hint="Untouched for this many days."
            >
              <Input
                id="archive-days" type="number" min={1} value={days}
                onChange={(e) => setDays(Number(e.target.value) || 30)}
              />
            </Field>
          </div>
          <Button onClick={() => archiveDone.mutate()} loading={archiveDone.isPending}>
            Archive them
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--line)] pt-4">
          <Button onClick={() => archiveProject.mutate()} loading={archiveProject.isPending}>
            {project.archivedAt ? 'Restore project' : 'Archive project'}
          </Button>
          <span className="text-[12px] text-[var(--ink-3)]">
            Hides it from the sidebar. Nothing is deleted.
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--line)] pt-4">
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => {
              const typed = window.prompt(
                `Deleting “${project.name}” removes every task, comment and attachment in it. This cannot be undone.\n\nType the project key ${project.key} to confirm.`,
              )
              if (typed === project.key) remove.mutate()
              else if (typed !== null) toast.show('That key did not match — nothing was deleted')
            }}
          >
            Delete this project
          </Button>
          <span className="text-[12px] text-[var(--ink-3)]">
            Permanent. Every task and attachment goes with it.
          </span>
        </div>
      </div>
    </Section>
  )
}
